//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { useOtChainState } from '@react/redux/store'

import { logOT } from '@shared/loggingShared'

import React, { useEffect } from 'react'
import { LiquidityAsset } from '@shared/sharedTypes'

import { Box, Grid } from '@mui/material'
import { EthAddr } from '@shared/SchemaTypes'

import { yupEthAddrRequired } from '@shared/Enums'
import { ServiceConfigurationAbi } from '@shared/Abis'
import { ChainConfiguration, StageConfiguration } from '@shared/DeploymentConfigs'
import { ServiceConfigurationV5 } from '@typechain-types/contracts'
import { ethers, Contract } from 'ethers'
import { getLiquidityAssetForAddress } from '@shared/LiquidityAsset'
import { fetchLiquidityAssetsForChainConfig } from '@react/fetchers/chainFetchers'

export async function addLiquidityAsset(
  assetAddr: EthAddr,
  chainConfiguration: ChainConfiguration,
  stageConfiguration: StageConfiguration,
  signer: ethers.Signer
): Promise<LiquidityAsset[]> {
  const serviceConfigurationContract = new Contract(
    chainConfiguration.serviceConfigurationAddr,
    ServiceConfigurationAbi,
    signer
  ) as unknown as ServiceConfigurationV5
  const tx = await serviceConfigurationContract.setLiquidityAsset(assetAddr, true)
  await tx.wait()

  return fetchLiquidityAssetsForChainConfig(
    chainConfiguration,
    stageConfiguration.serverApiBaseUrl,
    stageConfiguration.restAPIKey,
    true
  )
}

export default function SystemSettingsManagement(): React.ReactElement {
  const { chainConfiguration, userConnection, stageConfiguration } = useOtChainState()
  const [liquidityAssets, setLiquidityAssets] = React.useState<LiquidityAsset[]>([])
  const [newAddress, setNewAddress] = React.useState<EthAddr>('')
  const [inProcess, setInProcess] = React.useState<boolean>(false)
  const [newLiquidityAsset, setNewLiquidityAsset] = React.useState<LiquidityAsset | undefined>(undefined)

  useEffect(() => {
    if (chainConfiguration && stageConfiguration) {
      logOT.info('SystemSettingsManagement')
      fetchLiquidityAssetsForChainConfig(
        chainConfiguration,
        stageConfiguration.serverApiBaseUrl,
        stageConfiguration.restAPIKey
      )
        .then((_liquidityAssets: LiquidityAsset[]) => {
          logOT.info('Liquidity Assets:', _liquidityAssets)
          setLiquidityAssets(_liquidityAssets)
        })
        .catch((error: any) => {
          logOT.error('Error fetching liquidity asset keys:', error)
        })
    }
  }, [chainConfiguration])

  async function handleSetNewAddress(str: string) {
    if (chainConfiguration) {
      setNewAddress(str)
      try {
        yupEthAddrRequired.isValid(str)
        const liquidityAsset = await getLiquidityAssetForAddress(str, chainConfiguration)
        if (liquidityAssets.find((coin) => coin.addr === liquidityAsset.addr)) {
          logOT.error('Liquidity Asset already exists')
          setNewLiquidityAsset(undefined)
          return
        }
        setNewLiquidityAsset(liquidityAsset)
      } catch (_error: unknown) {
        setNewLiquidityAsset(undefined)
      }
    }
  }
  async function handleAddNewAddress() {
    if (chainConfiguration && stageConfiguration && newLiquidityAsset && userConnection?.signer) {
      try {
        setInProcess(true)
        const newLiquidityAssets = await addLiquidityAsset(
          newLiquidityAsset.addr,
          chainConfiguration,
          stageConfiguration,
          userConnection?.signer
        )
        setLiquidityAssets(newLiquidityAssets)

        setNewLiquidityAsset(undefined)
        setNewAddress('')
      } catch (error) {
        logOT.error('Error adding new stable coin:', error)
      }
      setInProcess(false)
    }
  }

  return (
    <Box>
      <Grid container spacing={2}>
        {liquidityAssets.map((coin, index) => (
          <Grid item xs={12} key={index}>
            <Box border={1} padding={2} borderRadius={4}>
              <h3>{coin.name}</h3>
              <p>Symbol: {coin.symbol}</p>
              <p>Address: {coin.addr}</p>
            </Box>
          </Grid>
        ))}
      </Grid>
      {inProcess && (
        <Grid container>
          <Grid item xs={12}>
            <Box border={1} padding={2} borderRadius={4}>
              <h3>Processing...</h3>
            </Box>
          </Grid>
        </Grid>
      )}
      {!inProcess && (
        <Grid container>
          <Grid item xs={12} key="textfield">
            <Box border={1} padding={2} borderRadius={4}>
              <h3>Add Address of new liquidity Asset</h3>
              <input
                type="text"
                value={newAddress}
                onChange={(e) => {
                  handleSetNewAddress(e.target.value)
                }}
                placeholder="Enter new address"
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </Box>
          </Grid>
          {newLiquidityAsset && (
            <Grid item xs={12} key="newCoind">
              <Box border={1} padding={2} borderRadius={4}>
                <h3>{newLiquidityAsset.name}</h3>
                <p>Symbol: {newLiquidityAsset.symbol}</p>
                <p>Address: {newLiquidityAsset.addr}</p>
                <button
                  disabled={inProcess} // Disable button if in process
                  onClick={() => {
                    handleAddNewAddress()
                  }}
                  style={{
                    marginTop: '8px',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: '#007BFF',
                    color: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  Add Stable Coin
                </button>
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  )
}
